import React from 'react';
import { NextPage } from 'next';
import { GUIDE_QUERY, RELATED_GUIDES_QUERY } from '../client/graphql/queries/guide';
import { slugIt, buildArticleURLObject } from '../client/utils/links';
import { buildNotFound, buildPageGone } from '../client/utils/error/http/httpErrors';
import { assertNotNullOrUndefined } from '../client/utils/error/error';
import { ArticleType } from '../domain/articleType';
import {
  AdvFluid, AdvFluidType, AdvFluidVariant, Guide, NodeType, Target,
} from '../domain/graphcms';
import GuidePage, { GuidePageProps } from '../client/templates/common/guide-page/GuidePage';
import { RelatedArticleItem } from '../client/components/related-articles/RelatedArticles';
import { mapArticlesNodes } from '../client/graphql/mappers/articleMappers';
import { RELATED_NEWS_QUERY } from '../client/graphql/queries/news';
import { getLogger } from '../logger/isomorphic';
import { getRelated } from '../client/utils/article/articleUtils';
import { includes } from '../client/utils/graphcms/nodeUtils';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { withGetServerSideError, withError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';

export interface EducationProps {
  guide: Guide,
  relatedGuides: Omit<RelatedArticleItem, 'onClick'>[],
  relatedNews: Omit<RelatedArticleItem, 'onClick'>[],
  bottomBar: GuidePageProps['bottomBar'],
}

const logger = getLogger();

export const Education: NextPage<EducationProps> = ({
  guide,
  relatedGuides,
  relatedNews,
  bottomBar,
}) => (
  <GuidePage
    guide={guide}
    relatedGuides={relatedGuides}
    relatedNews={relatedNews}
    type={ArticleType.EDUCATION}
    bottomBar={bottomBar}
  />
);

export const getServerSideProps = withGetServerSideError<EducationProps>(async ({ query }) => {
  if ((query.slug as string).includes('}')) {
    throw buildPageGone(`Page with slug ${query.slug} is gone`);
  }

  const graphQLClient = initGraphQLClient();
  const [
    { bottomBar },
    { data: { guide } },
  ] = await Promise.all([
    importDynamicData('pages/education.yml'),
    graphQLClient.query<{ guide: Guide }>({
      query: GUIDE_QUERY,
      variables: { id: query.id },
    }),
  ]);

  assertNotNullOrUndefined(guide, buildNotFound(`Education guide with id "${query.id}" not found`));

  const slug = slugIt(guide.alternateTitle || guide.title);
  const redirectUrl = {
    ...query,
    id: guide.id,
    slug,
  };

  if (guide.target !== Target.AGENTE) {
    return {
      redirect: {
        destination: buildArticleURLObject(redirectUrl, ArticleType.GUIDE).as,
        permanent: true,
      },
    };
  }

  if (query.slug !== slug) {
    return {
      redirect: {
        destination: buildArticleURLObject(redirectUrl, ArticleType.EDUCATION).as,
        permanent: true,
      },
    };
  }

  const [relatedGuides, relatedNews] = await Promise.all([
    graphQLClient
      .query({
        query: RELATED_GUIDES_QUERY,
        variables: {
          skipId: guide.id,
          date: guide.published,
          count: 4,
          createdAt: guide.createdAt,
          target: [Target.AGENTE],
        },
      })
      .then(({ data }) => getRelated(data.older, data.newer, 4))
      .then((related) => mapArticlesNodes(related, ArticleType.EDUCATION))
      .catch((err) => {
        logger.warn({ err }, 'Error while quering related education guides');
        return [];
      }),
    graphQLClient
      .query({
        query: RELATED_NEWS_QUERY,
        variables: {
          date: guide.published,
          count: 4,
          createdAt: guide.createdAt,
        },
      })
      .then(({ data }) => getRelated(data.older, data.newer, 4))
      .then((related) => mapArticlesNodes(related, ArticleType.NEWS))
      .catch((err) => {
        logger.warn({ err }, 'Error while quering related news');
        return [];
      }),
  ]);

  const mainColumn = [...guide.mainColumn];

  if (!includes(guide.mainColumn, NodeType.ADV_FLUID)) {
    mainColumn.splice(1, 0, {
      __typename: NodeType.ADV_FLUID,
      id: Date.now().toString(),
      advFluidType: AdvFluidType.SELL_PROPERTY,
      variant: AdvFluidVariant.VARIANT_1,
    } as AdvFluid);
  }

  return {
    props: {
      guide: {
        ...guide,
        mainColumn,
      },
      relatedGuides,
      relatedNews,
      bottomBar,
    },
  };
});

export default withError(Education);
